import React, { useState, useEffect } from "react";
import jsPDF from "jspdf";
import { toast } from "react-toastify";
import Download from "../assets/images/karzameLogo.png";
import { getAddressFromCoordinates } from "./helper";
import Datetime from "react-datetime";
import moment from "moment";
import "react-datetime/css/react-datetime.css";
import apiClient from "./apiClient";

// Firebase persistence has been replaced with backend userController calls.

const normalizeEscortList = (value) => {
  if (Array.isArray(value)) {
    return value;
  }

  if (value && typeof value === "object") {
    return Object.values(value);
  }

  return [];
};

const getLatestEscortEntry = (user) => {
  const escortEntries = normalizeEscortList(user?.ShortDistance_Escort);
  if (!escortEntries.length) return null;
  return [...escortEntries].sort((a, b) => {
    const getMs = (val) => {
      const v = val?.currentTimeStamp || val?.tripendtime || val?.createdAt || 0;
      return typeof v === 'string' ? new Date(v).getTime() : v;
    };
    return getMs(b) - getMs(a);
  })[0];
};

const getStoredEscortReport = (user) => {
  const latestEscort = getLatestEscortEntry(user);
  return (
    user?.escortReport ||
    user?.latestEscort?.escortReport ||
    latestEscort?.escortReport ||
    latestEscort?.report ||
    null
  );
};

export default function EscortReport({ props, isOpen: isOpenProp, onClose }) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [admin, setAdmin] = useState("");
  const [timestamp, setTimestamp] = useState(new Date().toLocaleString());
  const [escortStatus, setEscortStatus] = useState("");
  const [chatTime, setChatTime] = useState("");
  const [phoneTime, setPhoneTime] = useState("");
  const [userResponse, setUserResponse] = useState("");
  const [responseTime, setResponseTime] = useState("");
  const [location, setLocation] = useState("");
  const [callMade, setCallMade] = useState("");
  const [callOutcome, setCallOutcome] = useState("");
  const [dispatchMade, setDispatchMade] = useState("No");
  const [dispatchTime, setDispatchTime] = useState("");
  const [securityDispatched, setSecurityDispatched] = useState("");
  const [isDataSaved, setIsDataSaved] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [escortStartTime, setEscortStartTime] = useState("");
  const [reportId, setReportId] = useState("");

  const userId = props?.userData?.userId || props?.userId;
  const currentTimeStamp = props?.userData?.currentTimeStamp || props?.currentTimeStamp;

  const isOpen = typeof isOpenProp === "boolean" ? isOpenProp : isModalOpen;

  useEffect(() => {
    const getlastAddress = async () => {
      const placeName = await getAddressFromCoordinates(
        props?.userData?.Latitude || props?.Latitude,
        props?.userData?.Longitude || props?.Longitude
      );
      if (placeName) {
        setLocation(placeName);
      }
    };
    getlastAddress();
  }, [
    props?.userData?.Latitude || props?.Latitude,
    props?.userData?.Longitude || props?.Longitude,
  ]);

  useEffect(() => {
    if (currentTimeStamp) {
      const formattedEscortStartTime = new Date(currentTimeStamp).toISOString().slice(0, 16);
      setEscortStartTime(formattedEscortStartTime);
    }
  }, [currentTimeStamp]);

  useEffect(() => {
    if (isOpen) {
      setTimestamp(new Date().toLocaleString());
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && userId) {
      resetForm();
      loadReportData(userId);
    }
  }, [isOpen, userId]);

  useEffect(() => {
    if (isOpen && !reportId) {
      setReportId(generateReportId());
    }
  }, [isOpen, reportId]);

  const resetForm = () => {
    setAdmin("");
    setEscortStatus("");
    setLocation("");
    setChatTime("");
    setPhoneTime("");
    setUserResponse("");
    setResponseTime("");
    setCallMade("");
    setCallOutcome("");
    setDispatchMade("No");
    setDispatchTime("");
    setSecurityDispatched("");
    setEscortStartTime("");
    setIsDataSaved(false);
    setErrorMessage("");
    setReportId("");
  };

  const generateReportId = () => {
    const uniqueId = `SDE-${Date.now()}-${Math.floor(Math.random() * 10000)}`;
    return uniqueId;
  };

  const applyReportData = (reportData) => {
    setReportId(reportData.reportId || "");
    setAdmin(reportData.admin || "");
    setEscortStatus(reportData.escortStatus || "");
    setLocation(reportData.location || "");
    setChatTime(reportData.chatTime || "");
    setPhoneTime(reportData.phoneTime || "");
    setUserResponse(reportData.userResponse || "");
    setResponseTime(reportData.responseTime || "");
    setCallMade(reportData.callMade || "");
    setCallOutcome(reportData.callOutcome || "");
    setEscortStartTime(reportData.escortStartTime || "");
    setDispatchMade(reportData.dispatchMade || "No");
    setDispatchTime(reportData.dispatchTime || "");
    setSecurityDispatched(reportData.securityDispatched || "");
    setIsDataSaved(true);
  };

  const loadReportData = async (targetUserId) => {
    try {
      const initialReportData = getStoredEscortReport(props);

      if (initialReportData) {
        applyReportData(initialReportData);
        return;
      }

      const response = await apiClient.get(`/users/${targetUserId}`);
      const user = response?.data || response?.data?.data || {};
      const fetchedReportData = getStoredEscortReport(user);

      if (!fetchedReportData) {
        return;
      }

      applyReportData(fetchedReportData);
    } catch (error) {
      // Keep the form usable even when the backend has no saved report yet.
    }
  };

  const handleClick = () => setIsModalOpen(true);

  const handleClose = () => {
    if (onClose) onClose();
    setIsModalOpen(false);
  };

  const handleSave = () => {
    if (!userId) {
      setErrorMessage("userId is required to save the report.");
      return;
    }

    if (
      !admin ||
      !escortStatus ||
      !chatTime ||
      !phoneTime ||
      !userResponse ||
      !callMade ||
      !callOutcome ||
      !location ||
      !escortStartTime ||
      !securityDispatched
    ) {
      setErrorMessage("Please fill out all required fields before saving.");
      return;
    }

    const nextReportId = reportId || generateReportId();
    if (!reportId) {
      setReportId(nextReportId);
    }

    const reportData = {
      reportId: nextReportId,
      userName: props?.userData?.userName || props?.userName,
      userPhone: props?.userData?.userPhone || props?.userPhone,
      admin: admin,
      timestamp: timestamp,
      location: location,
      escortStatus: escortStatus,
      chatTime: chatTime,
      phoneTime: phoneTime,
      userResponse: userResponse,
      responseTime: responseTime,
      callMade: callMade,
      callOutcome: callOutcome,
      escortStartTime: escortStartTime,
      dispatchMade: dispatchMade,
      dispatchTime: dispatchTime,
      securityDispatched: securityDispatched,
    };

    const escortEntries = normalizeEscortList(
      props?.userData?.ShortDistance_Escort || props?.ShortDistance_Escort
    );
    const updatedEscortEntries = [...escortEntries];
    const latestIndex = updatedEscortEntries.length - 1;

    if (latestIndex >= 0) {
      updatedEscortEntries[latestIndex] = {
        ...updatedEscortEntries[latestIndex],
        escortReport: reportData,
      };
    }

    apiClient
      .put(`/users/${userId}`, {
        escortReport: reportData,
        ...(latestIndex >= 0 ? { ShortDistance_Escort: updatedEscortEntries } : {}),
      })
      .then(() => {
        setIsDataSaved(true);
        setErrorMessage("");
        toast.info(`Report saved successfully`, {
          autoClose: 3000,
          position: toast.POSITION.TOP_RIGHT,
        });
      })
      .catch(() => {
        setErrorMessage("Error saving report in backend.");
      });
  };

  const handleDownload = () => {
    const doc = new jsPDF();

    doc.addImage(Download, "PNG", 90, 10, 30, 30);

    doc.setFontSize(18);
    doc.setFont("helvetica", "bold");
    doc.text("Short Distance Escort Report", 105, 50, null, null, "center");

    doc.setFontSize(15);
    doc.setFont("helvetica", "bold");
    doc.text("Section 1: Report Metadata", 20, 70);
    doc.setFontSize(12);
    doc.setFont("helvetica", "normal");
    doc.text(`Report ID: ${reportId}`, 20, 80);
    doc.text(`User Name: ${props?.userData?.userName || props?.userName}`, 20, 90);
    doc.text(`Phone Number: ${props?.userData?.userPhone || props?.userPhone}`, 20, 100);
    doc.text(`Admin on Duty: ${admin}`, 20, 110);
    doc.text(`Report Timestamp: ${timestamp}`, 20, 120);

    doc.setFontSize(15);
    doc.setFont("helvetica", "bold");
    doc.text("Section 2: Escort Details", 20, 140);
    doc.setFontSize(12);
    doc.setFont("helvetica", "normal");
    doc.text(`Escort Start Time: ${escortStartTime}`, 20, 150);
    doc.text(`Last Known GPS Location: ${location}`, 20, 160);
    doc.text(`Escort Status: ${escortStatus}`, 20, 170);

    doc.setFontSize(15);
    doc.setFont("helvetica", "bold");
    doc.text("Section 3: Wellbeing Check Actions", 20, 190);
    doc.setFontSize(12);
    doc.setFont("helvetica", "normal");
    doc.text(`Chat Message Sent At: ${chatTime}`, 20, 200);
    doc.text(`User Response Received: ${userResponse}`, 20, 210);
    doc.text(`Response Time: ${userResponse === "Yes" ? responseTime : "No Response"}`, 20, 220);
    doc.text(`Phone Call Attempt: ${phoneTime}`, 20, 230);
    doc.text(`Call Status: ${callMade}`, 20, 240);
    doc.text(`Outcome: ${callOutcome}`, 20, 250);
    doc.text(`Dispatch Initiated: ${dispatchMade}`, 20, 260);

    if (dispatchMade === "Yes" && dispatchTime) {
      doc.text(`Time Dispatched: ${dispatchTime}`, 20, 270);
    }

    doc.text(
      `Security Agent Assigned: ${securityDispatched}`,
      20,
      dispatchMade === "Yes" && dispatchTime ? 280 : 270
    );

    doc.save("Escort_Report.pdf");
  };

  return (
    <div>
      {typeof isOpenProp !== "boolean" && (
        <button onClick={handleClick} className="btn btn-danger btn-sm">
          Escort Report
        </button>
      )}

      {isOpen && (
        <div style={styles.overlay}>
          <div style={styles.modal}>
            <h2 style={styles.header}>Short Distance Escort Report</h2>

            {errorMessage && <div style={styles.error}>{errorMessage}</div>}

            <div style={styles.section}>
              <h4 style={styles.sectionTitle}>Section 1: Report Metadata</h4>
              <div style={styles.row}>
                <label>Report ID: {reportId}</label>
              </div>
              <div style={styles.row}>
                <label>User Name: {props?.userData?.userName || props?.userName}</label>
              </div>
              <div style={styles.row}>
                <label>Phone Number: {props?.userData?.userPhone || props?.userPhone}</label>
              </div>
              <div style={styles.row}>
                <label>Admin on Duty: </label>
                <input
                  type="text"
                  value={admin}
                  onChange={(e) => setAdmin(e.target.value)}
                  placeholder="Enter admin name"
                  style={styles.input}
                />
              </div>
              <div style={styles.row}>
                <label>Time & Date of Report: {timestamp}</label>
              </div>
            </div>

            <div style={styles.section}>
              <h4 style={styles.sectionTitle}>Section 2: Escort Details</h4>
              <div style={styles.row}>
                <label>Escort Start Time:</label>
                <Datetime
                  value={escortStartTime ? moment(escortStartTime) : ""}
                  onChange={(date) => setEscortStartTime(moment(date).format("YYYY-MM-DDTHH:mm"))}
                  dateFormat="YYYY-MM-DD"
                  timeFormat="HH:mm A"
                  inputProps={{
                    placeholder: "YYYY-MM-DD HH:mm",
                    style: styles.input,
                  }}
                />
              </div>
              {/* <div style={styles.row}>
                <label>Last Known GPS Location:</label>
                <input
                  type="text"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  style={styles.input}
                />
              </div> */}
              <div style={styles.row}>
                <label>Last Known GPS Location:</label>
                <input
                  type="text"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  onClick={() => {
                    if (location) {
                      window.open(location, "_blank");
                    }
                  }}
                  style={{
                    ...styles.input,
                    cursor: location ? "pointer" : "default",
                    color: "#007BFF",
                    textDecoration: "underline",
                  }}
                  /// readOnly
                />
              </div>

              <div style={styles.row}>
                <label>Escort Status Before Alert: </label>
                <select
                  value={escortStatus}
                  onChange={(e) => setEscortStatus(e.target.value)}
                  style={styles.input}
                >
                  <option value="">Select Status</option>
                  <option value="In-motion">In-motion</option>
                  <option value="Delayed">Delayed</option>
                  <option value="Active">Active</option>
                </select>
              </div>
            </div>

            <div style={styles.section}>
              <h4 style={styles.sectionTitle}>Section 3: Wellbeing Check Actions</h4>

              <div style={styles.row}>
                <label>Chat Message Attempt:</label>
                <Datetime
                  value={chatTime ? moment(chatTime) : ""}
                  onChange={(date) => setChatTime(moment(date).format("YYYY-MM-DDTHH:mm"))}
                  dateFormat="YYYY-MM-DD"
                  timeFormat="HH:mm A"
                  inputProps={{
                    placeholder: "YYYY-MM-DD HH:mm",
                    style: styles.input,
                  }}
                />
              </div>

              <div style={styles.row}>
                <label>User Response:</label>
                <select
                  value={userResponse}
                  onChange={(e) => {
                    const value = e.target.value;
                    setUserResponse(value);
                    setResponseTime(value === "No" ? "No Response" : "");
                  }}
                  style={styles.input}
                >
                  <option value="">Select</option>
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </select>
              </div>

              {userResponse === "Yes" && (
                <div style={styles.row}>
                  <label>Response Time:</label>
                  <Datetime
                    value={responseTime ? moment(responseTime) : ""}
                    onChange={(date) => setResponseTime(moment(date).format("YYYY-MM-DDTHH:mm"))}
                    dateFormat="YYYY-MM-DD"
                    timeFormat="HH:mm A"
                    inputProps={{
                      placeholder: "YYYY-MM-DD HH:mm",
                      style: styles.input,
                    }}
                  />
                </div>
              )}

              {userResponse === "No" && (
                <div style={styles.row}>
                  <label>Response Time:</label>
                  <span>No Response</span>
                </div>
              )}

              <div style={styles.row}>
                <label>Phone Call Attempt:</label>
                <Datetime
                  value={phoneTime ? moment(phoneTime) : ""}
                  onChange={(date) => setPhoneTime(moment(date).format("YYYY-MM-DDTHH:mm"))}
                  dateFormat="YYYY-MM-DD"
                  timeFormat="HH:mm A"
                  inputProps={{
                    placeholder: "YYYY-MM-DD HH:mm",
                    style: styles.input,
                  }}
                />
              </div>

              <div style={styles.row}>
                <label>Call Status:</label>
                <select
                  value={callMade}
                  onChange={(e) => setCallMade(e.target.value)}
                  style={styles.input}
                >
                  <option value="">Select</option>
                  <option value="Ringing">Ringing</option>
                  <option value="Switched Off">Switched Off</option>
                  <option value="No Answer">No Answer</option>
                </select>
              </div>

              <div style={styles.row}>
                <label>Outcome:</label>
                <select
                  value={callOutcome}
                  onChange={(e) => setCallOutcome(e.target.value)}
                  style={styles.input}
                >
                  <option value="">Select</option>
                  <option value="User Answered">User Answered</option>
                  <option value="No Response">No Response</option>
                  <option value="Phone Off">Phone Off</option>
                </select>
              </div>

              <div style={styles.row}>
                <label>Dispatch Initiated:</label>
                <select
                  value={dispatchMade}
                  onChange={(e) => setDispatchMade(e.target.value)}
                  style={styles.input}
                >
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </select>
              </div>

              {dispatchMade === "Yes" && (
                <div style={styles.row}>
                  <label>Time Dispatched:</label>
                  <Datetime
                    value={dispatchTime ? moment(dispatchTime) : ""}
                    onChange={(date) => setDispatchTime(moment(date).format("YYYY-MM-DDTHH:mm"))}
                    dateFormat="YYYY-MM-DD"
                    timeFormat="HH:mm A"
                    inputProps={{
                      placeholder: "YYYY-MM-DD HH:mm",
                      style: styles.input,
                    }}
                  />
                </div>
              )}

              <div style={styles.row}>
                <label>Security Agent Assigned:</label>
                <select
                  value={securityDispatched}
                  onChange={(e) => setSecurityDispatched(e.target.value)}
                  style={styles.input}
                >
                  <option value="">Select</option>
                  <option value="Dispatched">Dispatched</option>
                  <option value="Not Dispatched">Not Dispatched</option>
                </select>
              </div>
            </div>

            <div style={styles.actions}>
              <button className="btn btn-secondary btn-sm" onClick={handleClose}>
                Close
              </button>
              <button className="btn btn-primary btn-sm" onClick={handleSave}>
                Save
              </button>

              {isDataSaved && (
                <button className="btn btn-success btn-sm" onClick={handleDownload}>
                  Download Report
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100vw",
    height: "100vh",
    backgroundColor: "rgba(0, 0, 0, 0.4)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 999,
  },
  modal: {
    background: "#fff",
    padding: "30px",
    width: "600px",
    maxHeight: "90vh",
    overflowY: "auto",
    borderRadius: "8px",
    boxShadow: "0 0 15px rgba(0,0,0,0.3)",
  },
  header: {
    marginBottom: "20px",
    fontSize: "22px",
    textAlign: "center",
  },
  section: {
    marginBottom: "25px",
    borderBottom: "1px solid #ddd",
    paddingBottom: "15px",
  },
  sectionTitle: {
    marginBottom: "10px",
    fontSize: "16px",
    fontWeight: "bold",
    color: "#444",
  },
  row: {
    display: "flex",
    flexDirection: "row",
    marginBottom: "12px",
    alignItems: "center",
    gap: "10px",
  },
  input: {
    padding: "8px",
    fontSize: "14px",
    border: "1px solid #ccc",
    borderRadius: "4px",
    flex: 1,
  },
  error: {
    color: "red",
    marginBottom: "15px",
    fontSize: "14px",
    textAlign: "center",
  },
  actions: {
    display: "flex",
    justifyContent: "space-between",
    marginTop: "20px",
  },
};
